// SUIVI D'UNE HYPOTHÈSE (migration 051), sous l'opportunité d'un insight.
//
// Trois choses, dans l'ordre où on s'en sert :
//   1. les TESTS qui la vérifient (plusieurs : on compare des versions) ;
//   2. l'AVANT/APRÈS de la mesure suivie, calculé par le serveur ;
//   3. les DÉCISIONS : lancer, puis trancher avec une phrase de justification.
//      Chacune s'écrit au journal, affiché en dessous.
//
// L'avant/après PROPOSE un verdict ; c'est une personne qui tranche.
import { useId, useState } from 'react';
import { useChangeHypothesisStatus, useDecisions, useTests, useUpdateInsight } from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { HypothesisStatus, Insight } from '../api/types';
import { Button, Chip, ErrorBanner, type ChipState } from '../components/ui';
import { HYPOTHESIS_STATUS_LABEL, OUTCOME_LABEL } from './labels';

/** Les mêmes passages que le serveur (`insights/verdict.js`). */
const NEXT: Record<HypothesisStatus, HypothesisStatus[]> = {
  a_tester: ['en_test', 'abandonnee'],
  en_test: ['confirmee', 'infirmee', 'abandonnee'],
  confirmee: ['a_tester'],
  infirmee: ['a_tester'],
  abandonnee: ['a_tester'],
};
const ACTION_LABEL: Record<HypothesisStatus, string> = {
  a_tester: 'Rouvrir',
  en_test: 'Lancer le test',
  confirmee: 'Confirmer',
  infirmee: 'Infirmer',
  abandonnee: 'Abandonner',
};
const NEEDS_NOTE: HypothesisStatus[] = ['confirmee', 'infirmee', 'abandonnee'];

const num = (n: number) => String(n).replace('.', ',');
/** Le taux d'un test arrive parfois en texte (colonne `numeric`) : on le lit comme un nombre. */
const rate = (v: number | string | null) => (v === null || v === undefined || v === '' ? null : Number(v));

// Où en est l'hypothèse : un état, dit par une pastille d'état (avec pictogramme).
// « À tester » reste neutre.
const TRACKING_STATE: Partial<Record<HypothesisStatus, ChipState>> = {
  en_test: 'avertissement',
  confirmee: 'succes',
  infirmee: 'avertissement',
  abandonnee: 'vide',
};

/** Un test se désigne par son code, sinon par ce qu'il teste. */
const testName = (t: { id: string; code: string | null; objective: string | null }) => t.code ?? t.objective ?? t.id;
const pct = (p: number) => `${Math.round(p * 100)} %`;
const dayTime = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

export function HypothesisTracking({ insight, projectId, canWrite }: { insight: Insight; projectId: string; canWrite: boolean }) {
  const tests = useTests(projectId).data ?? [];
  const decisions = (useDecisions(projectId).data ?? []).filter((d) => d.insight_id === insight.id);
  const update = useUpdateInsight(projectId);
  const change = useChangeHypothesisStatus(projectId);
  const [pending, setPending] = useState<HypothesisStatus | null>(null);
  const [note, setNote] = useState('');
  const noteId = useId();

  const status = insight.hypothesis_status;
  const tracking = insight.tracking;
  const outcome = tracking?.outcome ?? null;
  const metric = insight.success_metric;
  const unit = metric?.unit ? ` ${metric.unit}` : '';
  const linked = tests.filter((t) => insight.test_ids.includes(t.id));
  const best = linked.length > 1
    ? linked.reduce((a, b) => ((rate(b.success_rate) ?? -1) > (rate(a.success_rate) ?? -1) ? b : a))
    : null;
  const editableTests = canWrite && (status === 'a_tester' || status === 'en_test');

  const toggleTest = (id: string) => update.mutate({
    id: insight.id,
    patch: { testIds: insight.test_ids.includes(id) ? insight.test_ids.filter((t) => t !== id) : [...insight.test_ids, id] },
  });

  async function decide(to: HypothesisStatus) {
    if (NEEDS_NOTE.includes(to) && pending !== to) { setPending(to); return; }
    try {
      await change.mutateAsync({ id: insight.id, status: to, ...(note.trim() ? { note: note.trim() } : {}) });
      setPending(null);
      setNote('');
    } catch {
      /* rendu par le bandeau */
    }
  }

  return (
    <div className="tracking">
      <div className="opportunity-head">
        <h4 className="problem-label">Suivi de l’hypothèse</h4>
        <Chip state={TRACKING_STATE[status]}>{HYPOTHESIS_STATUS_LABEL[status]}</Chip>
      </div>

      {/* 1. Les tests qui la vérifient. */}
      <div className="tracking-block">
        <span className="problem-label">Tests</span>
        {editableTests && tests.length > 0 ? (
          <div className="opportunity-targets">
            {tests.map((t) => (
              <label key={t.id} className="opportunity-target">
                <input className="zy-check" type="checkbox" checked={insight.test_ids.includes(t.id)} disabled={update.isPending} onChange={() => toggleTest(t.id)} />
                {testName(t)}{rate(t.success_rate) !== null ? ` · ${num(rate(t.success_rate)!)} %` : ''}
              </label>
            ))}
          </div>
        ) : linked.length > 0 ? (
          <ul className="tracking-tests">
            {linked.map((t) => (
              <li key={t.id}>
                {testName(t)}{t.code && t.objective ? ` — ${t.objective}` : ''} :{' '}
                <strong>{rate(t.success_rate) !== null ? `${num(rate(t.success_rate)!)} % de réussite` : 'pas encore noté'}</strong>
                {best && best.id === t.id ? <Chip>Meilleure version</Chip> : null}
              </li>
            ))}
          </ul>
        ) : (
          <span className="problem-formula">{tests.length === 0 ? 'Aucun test utilisateur dans ce projet.' : 'Aucun test rattaché.'}</span>
        )}
        {editableTests && best ? (
          <span className="problem-formula">Meilleure version à ce jour : {testName(best)}.</span>
        ) : null}
      </div>

      {/* 2. L'avant/après. */}
      <div className="tracking-block">
        <span className="problem-label">Avant / après</span>
        {!metric ? (
          <span className="problem-formula">Définissez d’abord une métrique de succès.</span>
        ) : !metric.key ? (
          <span className="problem-formula">Reliez la métrique à une mesure suivie (« Modifier ») pour calculer l’avant/après.</span>
        ) : tracking?.awaiting ? (
          <span className="problem-formula">
            Départ figé à {metric.baseline !== null ? `${num(metric.baseline)}${unit}` : '—'} au lancement.
            En attente d’un nouveau relevé de la mesure pour mesurer l’effet.
          </span>
        ) : outcome ? (
          <span className={`tracking-outcome is-${outcome.level}`}>
            Départ {num(outcome.baseline)}{unit} → relevé {num(outcome.current)}{unit} · cible {num(outcome.target)}{unit}
            {' · '}<strong>{OUTCOME_LABEL[outcome.level]}</strong> ({pct(outcome.progress)} du chemin)
          </span>
        ) : (
          <span className="problem-formula">
            {tracking?.current ? `Dernier relevé : ${num(tracking.current.value)}${unit}. ` : 'Aucun relevé pour cette mesure. '}
            {metric.baseline === null ? 'La valeur de départ sera figée au lancement du test.' : 'Il manque une cible.'}
          </span>
        )}
      </div>

      {/* 3. Décider. */}
      {canWrite ? (
        <div className="tracking-block">
          {status === 'en_test' && outcome?.suggestion ? (
            <span className="problem-formula">L’avant/après propose : {ACTION_LABEL[outcome.suggestion].toLowerCase()}. À vous de trancher.</span>
          ) : null}
          {pending ? (
            <div className="problem-field">
              <label className="problem-label" htmlFor={noteId}>Pourquoi : {ACTION_LABEL[pending].toLowerCase()}</label>
              <textarea id={noteId} className="zy-input" rows={2} value={note}
                placeholder="Ce qui justifie la décision, en une phrase." onChange={(e) => setNote(e.target.value)} />
            </div>
          ) : null}
          <div className="opportunity-actions">
            {pending ? (
              <>
                <Button variant="accent" loading={change.isPending} disabled={!note.trim()} onClick={() => void decide(pending)}>
                  {ACTION_LABEL[pending]}
                </Button>
                <Button variant="ghost" onClick={() => { setPending(null); setNote(''); }}>Annuler</Button>
              </>
            ) : NEXT[status].map((to) => (
              <Button key={to} variant={to === 'en_test' || to === outcome?.suggestion ? 'primary' : 'ghost'}
                loading={change.isPending && change.variables?.status === to} onClick={() => void decide(to)}>
                {ACTION_LABEL[to]}
              </Button>
            ))}
          </div>
        </div>
      ) : null}
      {change.error ? <ErrorBanner>{errorMessage(change.error)}</ErrorBanner> : null}
      {update.error ? <ErrorBanner>Enregistrement impossible.</ErrorBanner> : null}

      {/* Le journal de cet insight. */}
      {decisions.length > 0 ? (
        <ol className="tracking-journal" aria-label="Journal des décisions">
          {decisions.map((d) => (
            <li key={d.id}>
              <span className="t-mono">{dayTime(d.decided_at)}{d.decided_by_name ? ` · ${d.decided_by_name}` : ''}</span>
              <span>
                {HYPOTHESIS_STATUS_LABEL[d.status_from]} → <strong>{HYPOTHESIS_STATUS_LABEL[d.status_to]}</strong>
                {d.metric?.outcome ? ` · ${OUTCOME_LABEL[d.metric.outcome.level]} (${num(d.metric.outcome.baseline)} → ${num(d.metric.outcome.current)})` : ''}
              </span>
              {d.note ? <span className="tracking-note">« {d.note} »</span> : null}
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}
